import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { observer } from 'mobx-react'
import PropTypes from 'prop-types'
import { useContext, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'

import { CalendarInput } from '@/components/CalendarInput'
import { ConfirmButton } from '@/components/ConfirmButton'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import { Switch } from '@/components/elements/switch'
import { MultiSelect } from '@/components/MultiSelect'
import { RichTextEditor } from '@/components/RichTextEditor'
import { SlidePanel } from '@/components/SlidePanel'
import { getCertificates } from '@/services/certificates.service'
import { getEntityRoles } from '@/services/entity.service'
import { addEvent, updateEvent, deleteEvent } from '@/services/events.service'
import { getTrainings } from '@/services/trainings.service'
import { UserStoreContext } from '@/stores/UserStore'
import { toast } from '@/utils/helpers'
import { formatUserName } from '@/utils/userHelpers'

dayjs.extend(customParseFormat)

const DEFAULT_VALUES = {
  name: '',
  date: '',
  startTime: '',
  endTime: '',
  capacity: 0,
  description: '',
  isTrainingEvent: true,
  instructors: [],
  certificates: [],
  additionalTrainings: [],
}

const EventSlideoutEdit = observer(({ editEvent, resetAndClose }) => {
  // Context
  const { activeEntity } = useContext(UserStoreContext)
  const form = useForm({
    defaultValues: DEFAULT_VALUES,
  })

  useEffect(() => {
    if (editEvent) {
      form.reset({
        ...editEvent,
        date: dayjs(editEvent.date).toDate(),
        startTime: dayjs(editEvent.startTime, 'HH:mm:ss').format('HH:mm'),
        endTime: dayjs(editEvent.endTime, 'HH:mm:ss').format('HH:mm'),
        certificates: editEvent.certificates?.map((c) => c.id ?? c) ?? [],
        instructors: editEvent.instructors?.map((i) => i.id ?? i) ?? [],
        additionalTrainings: editEvent.additionalTrainings?.map((t) => t.id ?? t) ?? [],
      })
    }
  }, [editEvent, form])

  // Tiptap editor sets focus on the rich text editor, this moves it to the name field.
  // See https://github.com/ueberdosis/tiptap/issues/4632
  useEffect(() => {
    const timer = setTimeout(() => {
      form.setFocus('name')
    }, 1)
    return () => clearTimeout(timer)
  }, [form])

  // Data Processing
  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()
  const { isPending, mutate: mutateEvent } = useMutation({
    mutationFn: (data) => (editEvent ? updateEvent(data) : addEvent(data)),
    onError: (err) => handleError(err.message),
    onSuccess: (data) => {
      handleSuccess(`${data.name} ${editEvent ? 'updated' : 'added'} successfully`)
      // Refresh event list
      queryClient.invalidateQueries({ queryKey: ['events'] })
      resetAndClose()
    },
  })

  const { isPending: deleteIsPending, mutate: deleteMutation } = useMutation({
    mutationFn: () => deleteEvent(editEvent.id),
    onError: (err) => handleError(err.message),
    onSuccess: () => {
      handleSuccess(`Event successfully deleted`)
      // Refresh event list
      queryClient.invalidateQueries({ queryKey: ['events'] })
      resetAndClose()
    },
  })

  const { data: instructors } = useQuery({
    queryKey: ['entityRoles', activeEntity, 'Instructor'],
    queryFn: () => getEntityRoles({ entityId: activeEntity, filters: { role: 'Instructor' } }),
    placeholderData: keepPreviousData,
  })

  const INSTRUCTORS = useMemo(() => {
    if (!instructors) return []

    return instructors.results.map((instructor) => {
      const label = `${formatUserName(instructor.user)} (${instructor.user?.email})`

      return { label, id: instructor.id }
    })
  }, [instructors])

  const certificateSorting = [{ id: 'name', desc: false }]
  const certificatePagination = {
    pageSize: 1000,
    pageIndex: 0,
  }

  const { data: certificates } = useQuery({
    queryKey: ['certificates', activeEntity, certificateSorting, certificatePagination],
    queryFn: () =>
      getCertificates({
        entityId: activeEntity,
        sorting: certificateSorting,
        pagination: certificatePagination,
      }),
  })

  const CERTIFICATES = useMemo(() => {
    if (!certificates) return []

    return certificates.results.map((certificate) => ({
      label: certificate.name,
      id: certificate.id,
    }))
  }, [certificates])

  const { data: trainings } = useQuery({
    queryKey: ['trainings'],
    queryFn: () => getTrainings({ fullList: true }),
    placeholderData: keepPreviousData,
  })

  const TRAININGS = useMemo(() => {
    if (!trainings) return []
    return trainings.results.map((training) => ({
      label: training.name,
      id: training.id,
    }))
  }, [trainings])

  /**
   * Handles form submission
   * @param {object} data
   */
  const onSubmit = async (data) =>
    mutateEvent({
      ...data,
      entity: activeEntity,
      date: dayjs(data.date).format('YYYY-MM-DD'),
      certificates: data.certificates?.map((c) => c.id ?? c) ?? [],
      instructors: data.instructors?.map((i) => i.id ?? i) ?? [],
      additionalTrainings: data.additionalTrainings?.map((t) => t.id ?? t) ?? [],
    })

  const onDelete = async () => {
    deleteMutation()
  }

  return (
    <SlidePanel
      actions={[
        {
          label: 'Cancel',
          onClick: () => resetAndClose(),
          type: 'cancel',
        },
        ...(editEvent
          ? [
              {
                asChild: true,
                label: (
                  <ConfirmButton
                    onClick={onDelete}
                    confirmTitle="Delete Event?"
                    loading={deleteIsPending}
                    confirmContent="Are you sure you want to delete this event?"
                  >
                    Delete Event
                  </ConfirmButton>
                ),
                type: 'delete',
              },
            ]
          : []),
        {
          label: `${editEvent ? 'Update' : 'Add'} Event`,
          onClick: form.handleSubmit(onSubmit),
          type: 'submit',
        },
      ]}
      content={
        <Form {...form}>
          <form className="flex flex-col gap-6" onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              disabled={isPending}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
              rules={{ required: 'This field is required' }}
            />

            <div className="flex flex-row gap-4">
              <FormField
                control={form.control}
                disabled={isPending}
                name="date"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>Date</FormLabel>
                    <FormControl>
                      <CalendarInput
                        inForm
                        fromYear={1900}
                        toYear={dayjs().year() + 2}
                        disabled={(date) => date < new Date('1900-01-01')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                rules={{ required: 'This field is required' }}
              />

              <FormField
                control={form.control}
                disabled={isPending}
                name="capacity"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>Capacity</FormLabel>
                    <FormControl>
                      <Input type="number" min="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex flex-row gap-4">
              <FormField
                control={form.control}
                disabled={isPending}
                name="startTime"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>Start Time</FormLabel>
                    <FormControl>
                      <Input
                        className="[&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                        type="time"
                        step={60}
                        defaultValue={field.value}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                rules={{ required: 'This field is required' }}
              />

              <FormField
                control={form.control}
                disabled={isPending}
                name="endTime"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>End Time</FormLabel>
                    <FormControl>
                      <Input
                        className="[&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                        type="time"
                        step={60}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                rules={{ required: 'This field is required' }}
              />
            </div>

            <FormField
              control={form.control}
              disabled={isPending}
              name="description"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <RichTextEditor
                      className="w-full"
                      editorContentClassName="p-5"
                      output="html"
                      label="Description"
                      placeholder="Enter your description..."
                      autofocus
                      editable
                      editorClassName="focus:outline-hidden"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              disabled={isPending}
              name="isTrainingEvent"
              render={({ field }) => (
                <FormItem className="w-full">
                  <div className="flex items-center space-x-2">
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormLabel>Training Event</FormLabel>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {form.watch('isTrainingEvent') && (
              <div className="flex flex-col gap-4">
                <FormField
                  control={form.control}
                  name="instructors"
                  render={({ field: requirementField }) => (
                    <FormItem>
                      <FormLabel>Instructor(s)</FormLabel>
                      <FormControl>
                        <MultiSelect
                          options={INSTRUCTORS}
                          placeholder="Select Instructors"
                          width="w-full"
                          {...requirementField}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                  rules={{ required: true }}
                />
                <FormField
                  control={form.control}
                  name="certificates"
                  render={({ field: requirementField }) => (
                    <FormItem>
                      <FormLabel>Course(s) Offered</FormLabel>
                      <FormControl>
                        <MultiSelect
                          options={CERTIFICATES}
                          placeholder="Select Courses"
                          width="w-full"
                          {...requirementField}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="additionalTrainings"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Additional Training(s)</FormLabel>
                      <FormControl>
                        <MultiSelect
                          options={TRAININGS}
                          placeholder="Select Trainings"
                          width="w-full"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}
          </form>
        </Form>
      }
      loading={isPending}
      title={`${editEvent ? 'Update' : 'Add'} Event`}
      setOpen={() => {}}
    />
  )
})

EventSlideoutEdit.propTypes = {
  editEvent: PropTypes.object,
  entities: PropTypes.array.isRequired,
  resetAndClose: PropTypes.func.isRequired,
  showXverseOptions: PropTypes.bool,
}

export default EventSlideoutEdit
